import { AlertMessageKind } from "client/Components";
import { ReactElement, useMemo } from "react";
import create from "zustand";

export type Alert = {
	id: number;
	content: string | ReactElement;
	kind: AlertMessageKind;
};

type AlertStateEntry = {
	alert: Alert;
	timerHandle: SetTimeoutHandle;
};

type ShowHandler = (
	kind: AlertMessageKind,
	content: string | ReactElement,
	timeoutInSeconds?: number
) => number;

type RemoveHandler = (id: number) => void;

type SpecificKindShowHandler = (content: string | ReactElement, timeoutInSeconds?: number) => number;

export interface AlertManager {
	show: ShowHandler;
	showInfo: SpecificKindShowHandler;
	showSuccess: SpecificKindShowHandler;
	showWarning: SpecificKindShowHandler;
	showError: SpecificKindShowHandler;
	remove: RemoveHandler;
}

type AlertStore = {
	alerts: readonly AlertStateEntry[];
	show: ShowHandler;
	remove: RemoveHandler;
};

const DEFAULT_TIMEOUT_IN_SECONDS: number = 5;
let idGenerator: number = 1;

const useAlertStore = create<AlertStore>(set => {
	const remove = (id: number): void => {
		set(
			(x) => {
				const copy: AlertStateEntry[] = [...x.alerts];
				const index: number = copy.findIndex(x => x.alert.id === id);

				if (index !== -1) {
					const state: AlertStateEntry = copy[index];
					clearTimeout(state.timerHandle);
					copy.splice(index, 1);
				}

				return {
					alerts: copy
				};
			}
		);
	};

	const show = (
		kind: AlertMessageKind,
		content: string | ReactElement,
		timeoutInSeconds?: number
	): number => {
		const id: number = idGenerator++;

		const timerHandle: SetTimeoutHandle = setTimeout(
			() => remove(id),
			(timeoutInSeconds ?? DEFAULT_TIMEOUT_IN_SECONDS) * 1000
		);

		const alert: Alert = {
			id,
			content,
			kind
		};

		const stateEntry: AlertStateEntry = {
			alert,
			timerHandle
		};

		set((x) => ({ alerts: [...x.alerts, stateEntry]}));

		return id;
	};

	return {
		alerts: [],
		show,
		remove
	};
});

export function useAlerts(): AlertManager {
	const show: ShowHandler = useAlertStore(x => x.show);
	const remove: RemoveHandler = useAlertStore(x => x.remove);

	const alertManager = useMemo(
		(): AlertManager => {
			return createAlertManager(show, remove);
		},
		[show, remove]
	);

	return alertManager;
}

export function useAlertCollection(): readonly Alert[] {
	const alerts: readonly AlertStateEntry[] = useAlertStore(x => x.alerts);
	return alerts.map(x => x.alert);
};

function createKindSpecificShowHandler(
	show: ShowHandler,
	kind: AlertMessageKind
): SpecificKindShowHandler {
	return (content: string | ReactElement, timeoutInSeconds?: number): number => {
		return show(kind, content, timeoutInSeconds);
	};
};

function createAlertManager(
	show: ShowHandler,
	remove: RemoveHandler
): AlertManager {
	return {
		show,
		remove,
		showInfo: createKindSpecificShowHandler(show, "info"),
		showSuccess: createKindSpecificShowHandler(show, "success"),
		showWarning: createKindSpecificShowHandler(show, "warning"),
		showError: createKindSpecificShowHandler(show, "error"),
	};
};

export function getAlertManagerOutsideReact(): AlertManager {
	const { show, remove } = useAlertStore.getState();
	return createAlertManager(show, remove);
}